import { Cancel, CloudUpload } from "@mui/icons-material";
import { Divider, IconButton, Paper, SxProps, TextField } from "@mui/material";
import { FC, KeyboardEventHandler, MouseEventHandler, useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import {
  removeFileAttachment,
  setFileAttachment,
} from "../../store/file-attachement-slice";
import store from "../../store/store";
import { FileAttachment } from "../model/file-attachement";
import { ExtendedAttributes } from "../model/form-configuration";

const paperStyle: SxProps = {
  display: "flex",
  bgcolor: "#ffffff",
};

const textStyle: SxProps = {
  ml: 1,
  flex: 1,
};

const iconStyle: SxProps = {
  p: "10px",
};

const dividerStyle: SxProps = {
  height: 28,
  m: 0.5,
};

const FileUpload: FC<{
  id: string;
  label: string;
  extendedAttributes: ExtendedAttributes;
}> = ({ id, label, extendedAttributes }) => { 
  const { control } = useFormContext();
  const { field } = useController({ name: id, control, defaultValue: "" });
  const [file, setFile] = useState<string>(String(field.value));
  const [fileSize, setFileSize] = useState<number>(-1);
  const [wrongExtension, setWrongExtension] = useState(false);
  let blobUrl: string = "";

  const handleKeyPress: KeyboardEventHandler<HTMLDivElement> = (event) => {
    handleClick();
    event.preventDefault();
  };

  const handleClick: () => void = () => {
    let input = document.createElement("input");
    input.type = "file";
    input.accept = extendedAttributes.allowedFileExtensions;
    input.onchange = (_this) => {
      handleFileSelection(input.files?.item(0)!);
    };
    input.click();
  };

  const handleFileSelection = (file: File) => {
    setWrongExtension(false);
    setFileSize(file.size!);
    if (
      file.size! <
      (extendedAttributes.maxFileSize as unknown as number) * 1000000
    ) {
      URL.revokeObjectURL(blobUrl);
      blobUrl = URL.createObjectURL(file);
      setFile(file.name!);
      store.dispatch(
        setFileAttachment({
          id: id,
          name: file.name,
          file: blobUrl,
          size: file.size,
        } as FileAttachment)
      );
      field.onChange(file.name);
    }
  };

  const handleReset: MouseEventHandler<HTMLButtonElement> = (event) => {
    setWrongExtension(false);
    setFileSize(-1);
    setFile("");
    URL.revokeObjectURL(blobUrl);
    store.dispatch(removeFileAttachment(id));
    field.onChange("");
  };

  return (
    <Paper sx={{ ...paperStyle }}>
      <TextField
        error={
          fileSize >
            (extendedAttributes.maxFileSize as unknown as number) * 1000000 ||
          wrongExtension
        }
        helperText={
          fileSize >
          (extendedAttributes.maxFileSize as unknown as number) * 1000000
            ? "Error: File Size greater than " +
              extendedAttributes.maxFileSize +
              " Mb"
            : wrongExtension
            ? "Error: Allowed Extensions(" +
              extendedAttributes.allowedFileExtensions +
              ")"
            : ""
        }
        id={id}
        sx={{ ...textStyle }}
        placeholder={label}
        inputProps={{
          "aria-label": label,
          readOnly: true,
        }}
        variant="standard"
        value={file}
        ref={field.ref}
        onBlur={field.onBlur}
        onChange={field.onChange}
        onClick={handleClick}
        onKeyPress={handleKeyPress}
        onDragOver={(e) => {
          e.stopPropagation();
          e.preventDefault();
          e.dataTransfer.dropEffect = "copy";
        }}
        onDrop={(e) => {
          e.stopPropagation();
          e.preventDefault();
          const accept = extendedAttributes.allowedFileExtensions
            .split(",")
            .map<boolean>(
              (ext: string) => e.dataTransfer.files.item(0)?.name.endsWith(ext)!
            );
          accept.find((a) => a)
            ? handleFileSelection(e.dataTransfer.files.item(0)!)
            : setWrongExtension(true);
        }}
      />
      <IconButton
        onClick={handleClick}
        type="button"
        sx={{ ...iconStyle }}
        aria-label="upload"
      >
        <CloudUpload />
      </IconButton>
      <Divider sx={{ ...dividerStyle }} orientation="vertical" />
      <IconButton
        onClick={handleReset}
        color="primary"
        sx={{ ...iconStyle }}
        aria-label="cancel"
      >
        <Cancel />
      </IconButton>
    </Paper>
  );
};

export default FileUpload;
//FileUpload.whyDidYouRender = true;